// contexts/LanguageContext.tsx
import React, { createContext, useEffect, useReducer, ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANG } from '../constants/all-language';
import { getToken } from '../utils/common';
import { setRoleURL } from '../utils/server';
import { getV1Languages } from '../api-client/api.system-feature/api';
// Relative on purpose: this module is part of the runtime graph that OWNS
// api-config, so it must not import it back through '@erpsquad/common/runtime'.
import { getApiConfig, isApiConfigInitialized } from '../utils/api-config';
import { useAuth } from './AuthContext';
// import ErpLoader from '../components/loader/erp-loader';

// Types
interface ILanguage {
  label: string;
  code: string;
  direction: string;
}

interface LanguageState {
  languages: ILanguage[];
  currentLanguage: ILanguage | null;
  isLoading: boolean;
  error: string | null;
  isFallbackActive: boolean; // Track if we're using fallback
}

export interface LanguageContextType extends LanguageState {
  changeLanguage: (langCode: string) => void;
  fetchLanguages: () => Promise<void>;
}

// Define fallback languages in order of preference - English first
const FALLBACK_LANGUAGES = [
  'en', // English as primary fallback
  'en-US', // US English as secondary
  DEFAULT_LANG, // Then default language
];

// Create English fallback language object
const createEnglishFallback = (): ILanguage => ({
  code: 'en',
  label: 'English',
  direction: 'ltr'
});

// Action types
type LanguageAction =
  | { type: 'FETCH_LANGUAGES_START' }
  | { type: 'FETCH_LANGUAGES_SUCCESS'; payload: ILanguage[] }
  | { type: 'FETCH_LANGUAGES_ERROR'; payload: string }
  | { type: 'SET_CURRENT_LANGUAGE'; payload: ILanguage; isFallback?: boolean }
  | { type: 'SET_FALLBACK_ACTIVE'; payload: boolean };

// Initial state
const initialState: LanguageState = {
  languages: [],
  currentLanguage: null,
  isLoading: false,
  error: null,
  isFallbackActive: false,
};

// Helper function to find the best available language
const findBestAvailableLanguage = (
  languages: ILanguage[],
  preferredCode?: string
): ILanguage | null => {
  if (!languages || languages.length === 0) return null;

  // First try the preferred language if provided
  if (preferredCode) {
    const preferred = languages.find(lang => lang.code === preferredCode);
    if (preferred) return preferred;
  }

  // Try fallback languages in order (English first)
  for (const fallbackCode of FALLBACK_LANGUAGES) {
    const fallback = languages.find(lang => lang.code === fallbackCode);
    if (fallback) return fallback;
  }

  // If no fallbacks found, return the first available language
  return languages[0] || null;
};

// Reducer
const languageReducer = (state: LanguageState, action: LanguageAction): LanguageState => {
  switch (action.type) {
    case 'FETCH_LANGUAGES_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'FETCH_LANGUAGES_SUCCESS':
      return {
        ...state,
        languages: action.payload,
        isLoading: false,
        error: null,
        isFallbackActive: false,
      };
    case 'FETCH_LANGUAGES_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        isFallbackActive: true,
      };
    case 'SET_CURRENT_LANGUAGE':
      return {
        ...state,
        currentLanguage: action.payload,
        isFallbackActive: action.isFallback || false,
      };
    case 'SET_FALLBACK_ACTIVE':
      return {
        ...state,
        isFallbackActive: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Provider component
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(languageReducer, initialState);
  const { i18n } = useTranslation();
  const { user, loading } = useAuth();

  // Initialize role URL - only when API config is available
  useEffect(() => {
    if (isApiConfigInitialized()) {
      setRoleURL(`${getApiConfig().backendBaseUrl}/rbac`);
    }
  }, []);

  // Guarded i18n changeLanguage call
  const safeChangeLanguage = async (langCode: string) => {
    if (i18n && typeof i18n.changeLanguage === 'function') {
      try {
        await i18n.changeLanguage(langCode);
      } catch (error) {
        console.warn(`Failed to change language to "${langCode}"`, error);
      }
    } else {
      console.warn(
        'i18n.changeLanguage is unavailable. Ensure I18nextProvider is configured in the host application.'
      );
    }
  };

  // Apply language settings
  const applyLanguageSettings = (language: ILanguage) => {
    document.body.dir = language.direction || 'ltr';
    safeChangeLanguage(language.code);
    localStorage.setItem('_language', language.code);
  };

  // Fetch languages from API
  const fetchLanguages = async (): Promise<void> => {
    try {
      dispatch({ type: 'FETCH_LANGUAGES_START' });

      const response = await getV1Languages({
        order: `id:1`,
        filters: `(is_active.in=[1])`,
        ...getToken(),
      });

      const data = response?.data?.languages || [];
      const languages = data
        ?.filter((d: any) => d?.is_active)
        ?.map((d: any) => ({
          label: d?.name,
          code: d?.code,
          direction: d?.direction,
        }));

      // Always ensure English is available in the languages list
      const hasEnglish = languages.some((lang: ILanguage) => lang.code === 'en');
      if (!hasEnglish) {
        languages.unshift(createEnglishFallback());
      }

      dispatch({ type: 'FETCH_LANGUAGES_SUCCESS', payload: languages });

      // Get saved language or use fallback
      const savedLangCode = localStorage.getItem('_language');
      const bestLanguage = findBestAvailableLanguage(languages, savedLangCode || undefined);

      if (bestLanguage) {
        const isFallback = savedLangCode && savedLangCode !== bestLanguage.code;
        dispatch({
          type: 'SET_CURRENT_LANGUAGE',
          payload: bestLanguage,
          isFallback
        });
        applyLanguageSettings(bestLanguage);

        // Log if we're using a fallback
        if (isFallback) {
          console.warn(`Saved language "${savedLangCode}" not available. Using fallback: "${bestLanguage.code}"`);
        }
      } else {
        console.error('No languages available');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch languages';
      dispatch({ type: 'FETCH_LANGUAGES_ERROR', payload: errorMessage });

      // On error, always fall back to English
      const englishFallback = createEnglishFallback();

      // Create languages array with English fallback for dropdown
      const fallbackLanguages = [englishFallback];

      dispatch({ type: 'FETCH_LANGUAGES_SUCCESS', payload: fallbackLanguages });
      dispatch({
        type: 'SET_CURRENT_LANGUAGE',
        payload: englishFallback,
        isFallback: true
      });

      document.body.dir = 'ltr';
      safeChangeLanguage('en');
      localStorage.setItem('_language', 'en');

      console.warn('Language fetch failed, using English fallback');
    }
  };

  // Change language function
  const changeLanguage = (langCode: string): void => {
    const selectedLanguage = state.languages.find((lang) => lang.code === langCode);

    if (selectedLanguage) {
      dispatch({ type: 'SET_CURRENT_LANGUAGE', payload: selectedLanguage });
      dispatch({ type: 'SET_FALLBACK_ACTIVE', payload: false });
      applyLanguageSettings(selectedLanguage);
    } else {
      console.error(`Language "${langCode}" not found in available languages`);

      // If language not found, fall back to English
      const englishFallback = createEnglishFallback();
      dispatch({
        type: 'SET_CURRENT_LANGUAGE',
        payload: englishFallback,
        isFallback: true
      });
      applyLanguageSettings(englishFallback);
    }
  };

  // Initialize on mount
  useEffect(() => {
    if(user?.id && !loading) {
      fetchLanguages();
    }
    //eslint-disable-next-line  
  }, [user?.id, loading]);

  const contextValue: LanguageContextType = {
    ...state,
    changeLanguage,
    fetchLanguages,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {/* {state.isLoading } */}
      {children}
    </LanguageContext.Provider>
  );
};

export { LanguageContext };
export default LanguageContext;

// Custom hook to use the LanguageContext
export const useLanguageContext = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
};
